/**
 * Recherche globale (Ctrl/⌘ K) : palette de commandes du design. Socle : navigation vers les métiers, modules,
 * sous-modules et référentiels autorisés. Les modules pourront y ajouter leurs objets (OT, tickets, clients…)
 * via un index de recherche serveur.
 */
import React from 'react';
import type { MetierId, NoeudCatalogue } from '@agora/shared';
import { CommandPalette, type CommandPaletteProps } from '../../design';
import { useT } from '../i18n';
import { useSocle } from '../contexte';
import { useActionsEspace, type OptionsOuvrir } from '../pages/espace';

const normaliser = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

export function Recherche({ fermer }: { fermer: () => void }) {
  const { t, lib } = useT();
  const { catalogue, preferences } = useSocle();
  const { ouvrir } = useActionsEspace();
  const [q, setQ] = React.useState('');
  const [actif, setActif] = React.useState(0);

  const routes = React.useMemo(() => new Map<string, string>(), []);
  const groupes = React.useMemo(() => {
    const cible = normaliser(q.trim());
    const garde = (libelle: string) => !cible || normaliser(libelle).includes(cible);
    const metiers: CommandPaletteProps['groups'][number]['items'] = [];
    const parcourir = (n: NoeudCatalogue, metier: MetierId, chemin: string[]) => {
      const l = lib(n.id);
      if (garde(l) || chemin.some(garde)) metiers.push({ id: n.id, label: l, icon: n.icone, metier, moduleLabel: chemin.length ? chemin[0] : l });
      routes.set(n.id, n.route);
      n.enfants.forEach((e) => parcourir(e, metier, [...chemin, l]));
    };
    (catalogue?.metiers || []).forEach((m) => parcourir(m, m.id as MetierId, []));
    // Outils personnels : seulement ceux que l'utilisateur a activés (messagerie : Réglages).
    const outils = (catalogue?.outils || []).filter((o) => o.id !== 'outils.messagerie' || preferences.messagerie);
    const autres = [...outils, ...(catalogue?.dimensions || []), ...(catalogue?.pilotage || [])]
      .filter((n) => garde(lib(n.id)))
      .map((n) => { routes.set(n.id, n.route); return { id: n.id, label: lib(n.id), icon: n.icone }; });
    return [
      { label: t('nav.metiers'), items: metiers.slice(0, 12) },
      { label: `${t('nav.outils')} · ${t('nav.referentiels')} · ${t('nav.pilotage')}`, items: autres.slice(0, 6) },
    ];
  }, [q, catalogue, preferences.messagerie, lib, t, routes]);

  const plats = groupes.flatMap((g) => g.items);
  const choisi = plats[Math.min(actif, plats.length - 1)];
  // Clic : dans la page active ; Ctrl/⌘ + clic ou Ctrl/⌘ + Entrée : nouvelle page.
  const aller = (id?: string, options?: OptionsOuvrir) => {
    const route = id ? routes.get(id) : undefined;
    if (route) { fermer(); ouvrir(route, options); }
  };

  // Fenêtre modale Radix (onClose) : voile, Échap, clic sur le voile, focus gardé puis rendu – voir CommandPalette.
  return (
    <CommandPalette
      ariaLabel={t('commun.rechercher')}
      placeholder={t('nav.rechercher')}
      hint="Esc"
      query={q}
      onQuery={(v) => { setQ(v); setActif(0); }}
      activeId={choisi?.id}
      groups={groupes}
      emptyLabel={t('commun.aucunResultat')}
      onSelect={aller}
      onClose={fermer}
      onKeyDown={(e) => {
        if (e.key === 'ArrowDown') { e.preventDefault(); setActif((a) => Math.min(a + 1, plats.length - 1)); }
        else if (e.key === 'ArrowUp') { e.preventDefault(); setActif((a) => Math.max(a - 1, 0)); }
        else if (e.key === 'Enter') { e.preventDefault(); aller(choisi?.id, { evenement: e }); }
      }}
    />
  );
}
